import { EventSource } from "eventsource";
import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useLatestRequest } from "@/composables/use-latest-request";
import config from "@/config";
import { isHttpExceptionModel, useServices } from "@/lib/openapi";
import type {
    AskAnswer,
    AskExchange,
    AskReference,
    AskRun,
    AskVersion,
    Suggestion,
    ThreadOffer,
} from "@/lib/openapi/gen/types.gen";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { notifyEntityChanged } from "@/stores/cache-invalidation";

/**
 * The ask box: one question about the current stek, and whatever became of it.
 *
 * The box used to be a single exchange discarded on close, because the answer
 * only existed for as long as the request that was fetching it. It no longer
 * works that way: the run happens in a worker and outlives the tab that started
 * it, so closing the box now *leaves the run going* and opening it again asks
 * the server what happened.
 *
 * What came before the current run is `history`, and it comes from the server
 * rather than being accumulated here. The box is one conversation per person per
 * stek, not per tab: a second tab, or the same tab after a refresh, has to see
 * the same sitting. A transcript the backend did not remember would look like
 * memory the box does not have.
 *
 * It is this sitting and no more. Nothing here persists across the record's TTL,
 * and the box that remembers you across days is a separate piece of work with
 * its own commitments about what may be kept.
 *
 * Nothing here holds a run id, because there isn't one. A run is identified by
 * who asked and where, which is why a browser that has forgotten everything can
 * still find its way back to an answer it asked for.
 */
/**
 * How long a box waits for the pills behind a finished answer before looking
 * once more on its own.
 *
 * The server stops promising them two minutes after the answer was stored
 * (`NOTICED_SETTLES_WITHIN_MS` on the record); this sits just past that, so
 * the one late look reads a record that has settled and the stream can close.
 * Counted from when this box *saw* the promise, which is at or after the
 * answer was stored — so the look is never early, and a box reopened late in
 * the gap simply looks a little later. It covers the two cases no nudge will
 * come for: a worker that died between the two writes, and a fetch that beat
 * the write to the replica.
 */
export const NOTICED_SETTLE_WAIT_MS = 125_000;

export const useAskStore = defineStore("ask", () => {
    // Its own services instance rather than the shared `api` singleton, which is
    // `throwOnError: true` — that makes every `response.ok` check dead code and
    // sends failures out as rejections. The box has its own surface to show a
    // failure on, so it wants the result, not an exception.
    const api = useServices({
        opts: { throwOnError: false },
        requires: { auth: true, organization: true, stek: true },
    });

    const isOpen = ref(false);
    /** The question as submitted — shown above the answer, while the input clears. */
    const askedQuestion = ref("");
    const answer = ref("");
    /**
     * The entities the answer is about, rendered as links by `AskReferences`.
     *
     * The backend resolved every one of these against the stek before sending
     * it, so an id that arrives here is already known to exist and to be
     * readable by this user — nothing downstream re-checks, and nothing should.
     */
    const references = ref<AskReference[]>([]);
    /**
     * Set only when the box declined a change; `AskThreadOffer` renders it as
     * the one button that starts the thread.
     */
    const offerThread = ref<ThreadOffer | null>(null);
    /**
     * What gotoko has noticed about the entities the live answer named — the
     * landing page's own cards, at most two, filtered by the backend to them.
     *
     * An offer for the *next* turn, so it lives here rather than on the
     * exchange: `beginRun` blanks it the moment another question is sent, and
     * a past exchange never shows one. Ignoring it costs nothing — the same
     * card is still on the landing page.
     */
    const noticed = ref<Suggestion[]>([]);
    /**
     * Which version of the business the live answer described — the shared
     * one, or the draft the asker was in — as the server recorded it when the
     * answer was written. Never worked out here from where the reader is
     * now: it is a record of what was read, and it goes with the answer into
     * the conversation. `null` while there is no answer, and for an answer
     * from before it was recorded, which shows no note.
     */
    const version = ref<AskVersion | null>(null);
    /**
     * The completed exchanges before the current run, oldest first.
     *
     * Rendered above the live one, so an answer to a clarifying question is
     * given with the question still on screen. Server-owned: the backend folds
     * a finished exchange in when the next question arrives, which is why
     * `show` can set this wholesale and nothing here has to work out whether
     * the current exchange is already in it.
     */
    const history = ref<AskExchange[]>([]);
    const isAsking = ref(false);
    const error = ref("");
    /**
     * What the run has done so far, in order. Arrives live over the stream, and
     * is replayed in full by `current` for anyone who opens the box mid-run —
     * so a latecomer sees what the live subscribers already saw rather than a
     * spinner over a blank.
     */
    const progressSteps = ref<string[]>([]);
    /**
     * Epoch milliseconds, from the server. The elapsed time has to count from
     * when the run really started, not from when this tab happened to arrive.
     */
    const startedAtMs = ref<number | null>(null);

    const latest = useLatestRequest();

    const GENERIC_FAILURE = "Something went wrong asking that. Try again.";

    /** The server's own sentence where there is one, ours where there is not. */
    const messageFrom = (failure: unknown): string =>
        (isHttpExceptionModel(failure)
            ? (failure.detail[0] as { message?: string })?.message
            : undefined) || GENERIC_FAILURE;

    const clear = () => {
        askedQuestion.value = "";
        answer.value = "";
        references.value = [];
        offerThread.value = null;
        noticed.value = [];
        version.value = null;
        history.value = [];
        error.value = "";
        isAsking.value = false;
        progressSteps.value = [];
        startedAtMs.value = null;
    };

    const showAnswer = (result: AskAnswer) => {
        // TIMED_OUT, RAN_OUT and NO_ANSWER all arrive here as ordinary answers,
        // phrased for a reader. They are not failures and must not be dressed as
        // one. They carry no references and offer no thread, so the two fields
        // below simply come back empty for them.
        answer.value = result.answer;
        references.value = result.references ?? [];
        offerThread.value = result.offer_thread ?? null;
        noticed.value = result.noticed ?? [];
        version.value = result.version ?? null;
    };

    /** Render a run the server handed us — the one place a run becomes screen. */
    const show = (run: AskRun) => {
        askedQuestion.value = run.question;
        startedAtMs.value = run.started_at_ms;
        progressSteps.value = run.progress ?? [];
        // Assigned rather than merged. The server's list is the sitting, capped
        // and ordered by the store that owns it; merging would let a tab that
        // had been closed through a supersede reintroduce an exchange the
        // backend had already dropped.
        history.value = run.history ?? [];
        error.value =
            run.status === "failed" ? run.error || GENERIC_FAILURE : "";
        isAsking.value = run.status === "running";
        if (run.answer) {
            showAnswer(run.answer);
        } else {
            answer.value = "";
            references.value = [];
            offerThread.value = null;
            noticed.value = [];
            version.value = null;
        }
    };

    // --- The live stream ----------------------------------------------------
    // Out-of-process by necessity: the run is in a worker, so there is nothing
    // in this browser's request to listen to. Mirrors `connectToThread`, down to
    // resolving the token inside the fetch override.

    let eventSource: EventSource | null = null;
    /** Which stek the open stream belongs to, so a stek switch cannot be missed. */
    let connectedStek: string | null = null;
    /** The one late look for pills that never came; see `NOTICED_SETTLE_WAIT_MS`. */
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const stopWaitingForPills = () => {
        if (settleTimer) {
            clearTimeout(settleTimer);
            settleTimer = undefined;
        }
    };

    /**
     * A finished answer whose pills are still on their way: keep the stream
     * for the nudge that brings them, and arm one late look in case it never
     * comes. Not a poll — one look, after the server has given up too.
     */
    const waitForPills = () => {
        stopWaitingForPills();
        settleTimer = setTimeout(() => {
            settleTimer = undefined;
            if (!isAsking.value) {
                void refresh();
            }
        }, NOTICED_SETTLE_WAIT_MS);
    };

    const disconnect = () => {
        stopWaitingForPills();
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
        connectedStek = null;
    };

    const connect = () => {
        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();
        const stekId = stek?.value?.identity ?? "";

        // Reuse the stream only while it is actually alive. EventSource retries
        // by itself after a transient network error, but a *fatal* response —
        // a 502 through the ingress mid-deploy, a 401 once a token is refused —
        // leaves it CLOSED and it never tries again. The object is still
        // assigned and the stek still matches, so without the readyState check
        // every later `open()` and `ask()` would return right here and no new
        // stream would ever be built: the box would stop receiving progress and
        // never learn the answer, silently, until someone closed and reopened
        // it. Memoising a dead connection is worse than not memoising at all.
        if (
            eventSource &&
            connectedStek === stekId &&
            eventSource.readyState !== EventSource.CLOSED
        ) {
            return;
        }
        disconnect();
        connectedStek = stekId;

        const source = new EventSource(`${config.bekendURI}/ask/events`, {
            // Resolve the token *inside* the fetch override: the eventsource lib
            // reuses this closure on every auto-reconnect, and the endpoint
            // re-authorizes each time. Firebase ID tokens expire ~1h, so
            // capturing once would let the stream silently 401 after expiry.
            fetch: async (input, init) => {
                const token = await getToken();
                return fetch(input, {
                    ...init,
                    headers: {
                        ...init?.headers,
                        Authorization: `Bearer ${token}`,
                        Organization: organization?.value?.identity ?? "",
                        Stek: stekId,
                    },
                });
            },
        });
        eventSource = source;

        // Fires on the initial connect and on every auto-reconnect. Pub/sub is
        // fire-and-forget, so this catches up on whatever was missed while
        // disconnected — which is the same fetch a reopen does.
        source.addEventListener("open", () => {
            void refresh();
        });

        // Same posture as the thread stream's: log without claiming a retry,
        // because whether there will be one depends on why this fired. A
        // transient error reconnects and re-fires `open`, which catches up; a
        // fatal one does not, and the next `open()` or `ask()` rebuilds the
        // stream because the guard above notices it is closed.
        source.addEventListener("error", () => {
            console.warn(
                "Ask SSE connection error; the browser reconnects if it was transient."
            );
        });

        source.addEventListener("message", ({ data }) => {
            let event: {
                type?: string;
                message?: string;
                changes?: {
                    entity_type?: string;
                    entity_id?: string;
                    attribute?: string;
                }[];
            };
            try {
                event = JSON.parse(data);
            } catch {
                return;
            }
            switch (event.type) {
                case "progress":
                    if (event.message) {
                        progressSteps.value = [
                            ...progressSteps.value,
                            event.message,
                        ];
                    }
                    break;
                case "started":
                    // Another of this user's tabs asked a new question — the run
                    // is keyed on the person, not on the tab. Everything on
                    // screen is now a reply to a question that has been
                    // superseded, so it goes and the new run is picked up.
                    //
                    // This reaches a box that is *watching* a run. A box that
                    // has settled on a finished answer has already disconnected
                    // (see `refresh`), so it keeps showing that answer until it
                    // is next opened — stale, but stale with something the
                    // person really did ask, and one open refreshes it. Holding
                    // a stream open on every idle box to close that gap costs
                    // more than the gap does.
                    progressSteps.value = [];
                    void refresh();
                    break;
                case "entities_changed": {
                    // The only event here that carries its own data. Everything
                    // else is a nudge to refetch the run, but what the box
                    // changed in the business is not part of that record and
                    // there is no endpoint to read it back from — so the page
                    // the person is standing on learns from the event which of
                    // its caches went stale.
                    //
                    // No `origin`: the change was made by a worker, which is
                    // external to every cache in this tab, and an absent origin
                    // is what the bus's consumers already read as external.
                    //
                    // Neither refreshes nor disconnects: the run is still going
                    // and its answer is still to come.
                    const changed = (event.changes ?? []).flatMap((change) =>
                        change.entity_type && change.entity_id
                            ? [
                                  {
                                      entityType: change.entity_type,
                                      entityId: change.entity_id,
                                      // Carried through where the event names
                                      // one: a process's step list is rebuilt
                                      // from its diagram, so the store holding
                                      // it narrows on `process_definition` and
                                      // would sit still for a bare change.
                                      ...(change.attribute
                                          ? { attribute: change.attribute }
                                          : {}),
                                  },
                              ]
                            : []
                    );
                    if (changed.length > 0) {
                        notifyEntityChanged(changed);
                    }
                    break;
                }
                case "finished":
                case "failed":
                case "noticed":
                    // The event carries no answer on purpose: fetching it is the
                    // same path a reopen takes, so there is one way an answer
                    // reaches the screen rather than two that can disagree.
                    // `noticed` is the same shape a beat later: what gotoko
                    // noticed has been attached to the answer, go and read it.
                    void refresh();
                    break;
            }
        });
    };

    /**
     * True between submitting a question and the server confirming it.
     *
     * In that window the POST's own response is the authority and a catch-up
     * fetch is not, because the record it would read may still be the *previous*
     * run — see `refresh`.
     */
    let starting = false;

    /** Ask the server what my run in this stek is doing, and render it. */
    const refresh = async () => {
        if (starting) {
            // The stream is opened just before the question is sent, so its
            // first `open` can fire while the POST is still in flight. Reading
            // the record then returns whatever was there *before* — the last
            // answer, or nothing — and rendering it would put a superseded
            // answer on screen under the question that just replaced it, where
            // it would sit looking like a reply until the run finished.
            return;
        }
        const { token } = latest.begin();
        try {
            const { response, data } =
                await api.currentAskAskMgtAnswerQuestionCurrentGet();
            if (!latest.isCurrent(token)) {
                return;
            }
            if (!response?.ok) {
                return;
            }
            if (data) {
                show(data);
                if (data.status === "running") {
                    stopWaitingForPills();
                } else if (data.noticed_pending) {
                    // Done, but what gotoko noticed is still being worked out —
                    // the answer went out first so nobody waited on that sweep.
                    // One more nudge to wait for, and one late look of our own
                    // in case it never comes.
                    waitForPills();
                } else {
                    // Nothing more will be published on this channel until the
                    // next question, and that question opens its own stream.
                    disconnect();
                }
            } else {
                // Never asked here, expired, or the store is unreachable. All
                // three mean the same thing to a box that is opening.
                //
                // The stream stays open, unlike the finished-run branch above,
                // and that asymmetry is deliberate: an empty box is one keystroke
                // from a question, and the connection it would need is already
                // standing. A finished answer is where someone stops. Either way
                // `close()` drops it, and neither holds a database connection.
                clear();
            }
        } catch {
            // A failed catch-up leaves whatever is on screen alone. The stream
            // is still connected, and the next event refetches.
        }
    };

    /**
     * Open the box and pick up whatever is already going.
     *
     * This is what makes closing it survivable: the fetch is the recovery, and
     * it is the same fetch the stream's reconnect does.
     */
    const open = () => {
        isOpen.value = true;
        connect();
        void refresh();
    };

    /**
     * Close the box and leave the run alone.
     *
     * The opposite of what this used to do. Closing used to abort the in-flight
     * request, because a run finishing into a closed box could not be shown and
     * nothing remembered it to show later. Both halves of that have changed: the
     * run is not this tab's to cancel, and the answer is waiting on the next
     * open. What is dropped is only the listening.
     */
    const close = () => {
        disconnect();
        isOpen.value = false;
        clear();
    };

    /**
     * Everything a new run needs on screen before the server has answered.
     *
     * Shared by both ways in, because the difference between them is only who
     * writes the question: someone typed it, or the server derived it from a
     * finding they clicked. Everything after that point is identical, which is
     * the same reason nothing on the backend below the endpoint changed.
     *
     * `shown` is what the box displays as the question while it waits. For a
     * typed question that is what they typed; for a suggestion it is a
     * placeholder, replaced by the server's own wording the moment the POST
     * answers.
     */
    const beginRun = (shown: string) => {
        // Fold the exchange on screen into the conversation before replacing
        // it, which is exactly what the server does when it takes the question.
        // Done here as well so the previous answer does not blink out of the
        // box for the length of the round trip and then come back; `show` then
        // replaces this with the server's own list, which is the authority.
        //
        // Only a finished one, matching the backend: a question still being
        // answered is not an exchange.
        if (askedQuestion.value && answer.value) {
            history.value = [
                ...history.value,
                {
                    question: askedQuestion.value,
                    answer: {
                        answer: answer.value,
                        references: references.value,
                        offer_thread: offerThread.value,
                        version: version.value,
                    },
                },
            ];
        }
        askedQuestion.value = shown;
        answer.value = "";
        references.value = [];
        offerThread.value = null;
        version.value = null;
        // The offer was for this turn; the person took another. Gone, not kept.
        noticed.value = [];
        progressSteps.value = [];
        error.value = "";
        isAsking.value = true;

        // Before the request, not after: the first progress step can land while
        // the POST is still in flight, and a stream opened afterwards would miss
        // it. The flag is what makes subscribing early safe — see `refresh`.
        starting = true;
        // A late look armed for the previous answer's pills has nothing left
        // to look for: this question replaces that record.
        stopWaitingForPills();
        connect();
    };

    /** Render what the server accepted, or say why it did not. */
    const settle = (
        token: symbol,
        response: { ok?: boolean } | undefined,
        data: AskRun | undefined,
        failure: unknown
    ): void => {
        if (!latest.isCurrent(token)) {
            return;
        }
        if (!response?.ok || !data) {
            // `throwOnError: false` means the client hands failures back rather
            // than raising them. The server's own sentence is sitting in
            // `error`, and saying "something went wrong" over the top of it
            // throws away the one case where the backend had something specific
            // to say — which for a suggestion is the whole message: the finding
            // has already been dealt with.
            //
            // What was asked stays on screen. The input line was cleared on
            // submit, so clearing this too would leave an error with no record
            // of what produced it and nothing to retry from. A suggestion has
            // nothing to leave — its question is the server's, and the server
            // is what refused — so it shows the refusal alone.
            error.value = messageFrom(failure);
            isAsking.value = false;
            disconnect();
            return;
        }
        // The run, not the answer — this returns as soon as the question has
        // been accepted. What lands here is `status: "running"` with the
        // server's start time, which is what the elapsed clock counts from, and
        // for a suggestion the question the server wrote.
        show(data);
    };

    const ask = async (question: string) => {
        const trimmed = question.trim();
        if (!trimmed || isAsking.value) {
            return;
        }

        const { token } = latest.begin();
        beginRun(trimmed);

        try {
            const {
                response,
                data,
                error: failure,
            } = await api.startAskAskMgtAnswerQuestionStartPost({
                body: { question: trimmed },
            });
            settle(token, response, data, failure);
        } catch (e) {
            // Narrow, and deliberately so. With `throwOnError: false` the client
            // rejects only for its own reasons — the request interceptor's
            // "Request requires stek" is the realistic one — because non-2xx
            // responses come back as values and are handled above.
            if (!latest.isCurrent(token)) {
                return;
            }
            console.error("Ask failed", e);
            error.value = messageFrom(e);
            isAsking.value = false;
            disconnect();
        } finally {
            // Whatever happened, the POST is no longer the authority. Cleared
            // even on the stale-token path, or a superseded question would leave
            // catch-up fetches suppressed for the rest of the session.
            starting = false;
        }
    };

    /**
     * Open the box already talking about a suggestion somebody clicked.
     *
     * **An identity goes up, never the words on the card.** The card was drawn
     * when the page was read and the finding may have been fixed since; the
     * server looks it up again and writes the question from what it finds. So
     * the question shown here is a placeholder for one round trip, and then it
     * is the server's.
     *
     * A finding that has gone comes back as a refusal with a sentence written
     * for a reader, and `settle` puts it where every other failure goes.
     */
    const askAboutSuggestion = async (
        suggestionId: string,
        issueKey?: string
    ) => {
        if (!suggestionId || isAsking.value) {
            return;
        }

        const { token } = latest.begin();
        isOpen.value = true;
        beginRun("");

        try {
            const {
                response,
                data,
                error: failure,
            } = await api.startAskAskMgtAskAboutSuggestionStartPost({
                // An identity, never a sentence — and only the key when the
                // caller clicked one finding rather than a rule's group.
                body: issueKey
                    ? { suggestion_id: suggestionId, issue_key: issueKey }
                    : { suggestion_id: suggestionId },
            });
            settle(token, response, data, failure);
        } catch (e) {
            if (!latest.isCurrent(token)) {
                return;
            }
            console.error("Ask about suggestion failed", e);
            error.value = messageFrom(e);
            isAsking.value = false;
            disconnect();
        } finally {
            starting = false;
        }
    };

    /**
     * What gotoko has noticed about one entity — for the box opened, empty, on
     * that entity's page.
     *
     * The same cards the landing page shows, filtered by the server to this
     * entity, at most two. Returned rather than kept on the store: they belong
     * to the page the box happens to be over, and the component that knows
     * which page that is owns them. Nothing here is an error a reader needs to
     * see — a card that could not be fetched is simply not offered.
     */
    const noticedAbout = async (entity: {
        entity_type: string;
        entity_id: string;
    }): Promise<Suggestion[]> => {
        try {
            const { response, data } =
                await api.startAskAskMgtNoticedAboutStartPost({
                    body: {
                        entity_type: entity.entity_type,
                        entity_id: entity.entity_id,
                    },
                });
            return response?.ok && data ? data : [];
        } catch {
            return [];
        }
    };

    return {
        isOpen,
        askedQuestion,
        answer,
        references,
        offerThread,
        noticed,
        version,
        history,
        isAsking,
        error,
        progressSteps,
        startedAtMs,
        open,
        close,
        ask,
        askAboutSuggestion,
        noticedAbout,
        refresh,
    };
});

export const useAskManager = () => storeToRefs(useAskStore());
